<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import {
  UserCircleIcon,
  UserIcon,
  GiftIcon,
  Cog6ToothIcon,
  ArrowRightStartOnRectangleIcon
} from '@heroicons/vue/24/outline'
import { useAuth } from '@/composables/useAuth'
import { useLocaleRoute } from '@/composables/useLocaleRoute'
import LanguageSelector from './LanguageSelector.vue'
import martinIcon from '@/assets/martin-icon.png'

const route = useRoute()
const { t } = useI18n()
const { isAuthenticated, user, logout, isAdmin } = useAuth()
const { localePath } = useLocaleRoute()

const navigation = computed(() => [
  { name: t('nav.challenges'), to: localePath('/challenges') },
  { name: t('nav.leaderboard'), to: localePath('/leaderboard') },
  { name: t('nav.pricing'), to: localePath('/pricing') },
])

const isActiveRoute = (path: string) => {
  return route.path === path || route.path.startsWith(path + '/')
}
</script>

<template>
  <header class="bg-white border-b border-gray-200 sticky top-0 z-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <!-- Logo -->
        <div class="flex items-center">
          <RouterLink :to="localePath('/challenges')" class="flex items-center space-x-2">
            <img :src="martinIcon" alt="Martin" class="h-8" />
            <span class="font-semibold text-lg text-gray-900">Martin Academy</span>
          </RouterLink>
        </div>

        <!-- Desktop Navigation -->
        <nav class="hidden md:flex items-center space-x-8">
          <RouterLink
            v-for="item in navigation"
            :key="item.to"
            :to="item.to"
            class="text-sm font-medium transition-colors"
            :class="isActiveRoute(item.to) ? 'text-primary-600' : 'text-gray-600 hover:text-gray-900'"
          >
            {{ item.name }}
          </RouterLink>
        </nav>

        <!-- Right side -->
        <div class="flex items-center space-x-4">
          <LanguageSelector />

          <template v-if="isAuthenticated">
            <!-- User Menu -->
            <Menu as="div" class="relative">
              <MenuButton class="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <img
                  v-if="user?.avatarUrl"
                  :src="user.avatarUrl"
                  :alt="user.name"
                  class="w-8 h-8 rounded-full"
                />
                <UserCircleIcon v-else class="w-8 h-8 text-gray-400" />
                <span class="hidden sm:block text-sm font-medium text-gray-700">{{ user?.displayName || user?.name }}</span>
              </MenuButton>

              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <MenuItems class="absolute right-0 mt-2 w-48 origin-top-right bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div class="py-1">
                    <MenuItem v-slot="{ active }">
                      <RouterLink
                        :to="localePath('/profile')"
                        :class="[active ? 'bg-gray-100' : '', 'flex items-center px-4 py-2 text-sm text-gray-700']"
                      >
                        <UserIcon class="w-4 h-4 mr-2" />
                        {{ t('user_menu.profile') }}
                      </RouterLink>
                    </MenuItem>
                    <MenuItem v-slot="{ active }">
                      <RouterLink
                        :to="localePath('/referral')"
                        :class="[active ? 'bg-gray-100' : '', 'flex items-center px-4 py-2 text-sm text-gray-700']"
                      >
                        <GiftIcon class="w-4 h-4 mr-2" />
                        {{ t('user_menu.referral') }}
                      </RouterLink>
                    </MenuItem>
                    <MenuItem v-if="isAdmin" v-slot="{ active }">
                      <RouterLink
                        :to="localePath('/admin/users')"
                        :class="[active ? 'bg-gray-100' : '', 'flex items-center px-4 py-2 text-sm text-gray-700']"
                      >
                        <Cog6ToothIcon class="w-4 h-4 mr-2" />
                        {{ t('user_menu.admin_panel') }}
                      </RouterLink>
                    </MenuItem>
                    <MenuItem v-slot="{ active }">
                      <button
                        @click="logout"
                        :class="[active ? 'bg-gray-100' : '', 'flex items-center w-full text-left px-4 py-2 text-sm text-gray-700']"
                      >
                        <ArrowRightStartOnRectangleIcon class="w-4 h-4 mr-2" />
                        {{ t('user_menu.sign_out') }}
                      </button>
                    </MenuItem>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </template>

          <template v-else>
            <RouterLink
              :to="localePath('/login')"
              class="btn-primary"
            >
              {{ t('user_menu.sign_in') }}
            </RouterLink>
          </template>
        </div>
      </div>
    </div>
  </header>
</template>
